import {Color} from '@benchling/nucleus-ui';
import React from 'react';

import {ExtensionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import staticURL from 'lib/staticURL';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import type {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {assertNever} from 'util/Types';

/** Default when no custom image; same glyph as Model Hub in the nav. */
const DEFAULT_MODEL_EXTENSION_ICON_NAME: BenchlingIconName = 'hexagon-nodes';
/** Default for MCP server extensions without a directory image; matches MCP tool default in chat. */
const DEFAULT_MCP_EXTENSION_ICON_NAME: BenchlingIconName = 'plug';
/** Default for agent skill extensions; matches the agent skill icon used elsewhere. */
const DEFAULT_AGENT_SKILL_EXTENSION_ICON_NAME: BenchlingIconName = 'wand-magic-sparkles';
const DEFAULT_EXTENSION_ICON_COLOR = Color.BLUE_400;

export const EXTENSION_ICON_PATH_BY_API_ID: Record<string, string> = {
  // Example: 'mcpdef_abc123': '/static/img/extensions/my-extension.svg',
  mcpdef_sAb9VeK44T: '/static/img/extensions/notion.png',
  mcpdef_hTkAxlKbB3: '/static/img/extensions/seqera.png',
  mcpdef_4Q1WnDm1rv: '/static/img/extensions/elicit.png',
  mcpdef_rHV4kHq8I9: '/static/img/extensions/quilt.svg',
  mcpdef_bhSVH5J5nv: '/static/img/extensions/gxl.svg',
  mcpdef_62aHBcHMIp: '/static/img/extensions/sharepoint.svg',
  mcpdef_VKl1Lo6zaM: '/static/img/extensions/snowflake.png',
  mcpdef_pKdLzQPoGo: '/static/img/extensions/pubmed.png',
  modeldef_Zv9aUGex3G: '/static/img/model_hub/alphafold2.png',
  modeldef_tcRVuJJQx0: '/static/img/model_hub/chai1.png',
  modeldef_9veMWuxLnK: '/static/img/model_hub/boltz2.png',
  modeldef_uUQAHk6MKh: '/static/img/model_hub/boltz2.png',
};

export interface GetExtensionIconOptions {
  className?: string;
  /** Chooses the fallback Benchling icon when there is no custom image in `EXTENSION_ICON_PATH_BY_API_ID`. */
  extensionType: ExtensionType;
}

/** Fallback Benchling icon when there is no custom image mapped for `apiId`. */
function defaultBenchlingIconForExtensionType(extensionType: ExtensionType): BenchlingIconName {
  switch (extensionType) {
    case ExtensionType.AGENT_SKILL:
      return DEFAULT_AGENT_SKILL_EXTENSION_ICON_NAME;
    case ExtensionType.MCP_SERVER:
      return DEFAULT_MCP_EXTENSION_ICON_NAME;
    case ExtensionType.MODEL:
      return DEFAULT_MODEL_EXTENSION_ICON_NAME;
    default:
      return assertNever(extensionType);
  }
}

export function getExtensionIcon(apiId: string, options: GetExtensionIconOptions): React.ReactElement {
  const path = EXTENSION_ICON_PATH_BY_API_ID[apiId] ?? null;

  if (path != null) {
    return <img src={staticURL(path)} alt="" className={options.className} />;
  }

  const iconName = defaultBenchlingIconForExtensionType(options.extensionType);

  return (
    <span className={options?.className} style={{color: DEFAULT_EXTENSION_ICON_COLOR}}>
      <BenchlingIcon icon={iconName} />
    </span>
  );
}
